import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { VehicleSafetyDetail } from '../../safety-dashboard.model';
import { SafetyDashboardStore } from '../../safety-dashboard.store';

type SortKey = 'SafetyScore' | 'DistanceKm' | 'MaxSpeed' | 'TotalEvents' | 'VeryHighRiskEvents' | null;

@Component({
  selector: 'app-safety-table',
  templateUrl: './safety-table.component.html',
  styleUrls: ['./safety-table.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SafetyTableComponent implements OnInit, OnDestroy {
  loading = false;

  searchText = '';
  statusFilter = '';
  qualityFilter = '';

  statusOptions = ['پرریسک', 'نیازمند توجه', 'نسبتاً ایمن', 'ایمن', 'داده ناکافی برای ارزیابی'];
  qualityOptions: string[] = [];

  private allRows: VehicleSafetyDetail[] = [];
  filteredRows: VehicleSafetyDetail[] = [];

  sortKey: SortKey = null;
  sortDir: 1 | -1 = 1;

  private sub = new Subscription();

  constructor(private store: SafetyDashboardStore, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.sub.add(this.store.loading$.subscribe((v) => { this.loading = v; this.cdr.markForCheck(); }));
    this.sub.add(
      this.store.vehicles$.subscribe((rows) => {
        this.allRows = rows || [];
        this.qualityOptions = distinctQualities(this.allRows);
        this.applyFilter();
      })
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  trackByUnit(_: number, x: VehicleSafetyDetail): string {
    return String(x.strUnitID);
  }

  applyFilter(): void {
    const qv = (this.searchText || '').trim().toLowerCase();
    const sf = this.statusFilter;
    const qf = this.qualityFilter;

    let out: VehicleSafetyDetail[];
    if (!sf && !qf && !qv) {
      out = this.allRows;
    } else {
      out = [];
      for (const x of this.allRows) {
        if (sf && x.SafetyStatus !== sf) continue;
        if (qf && x.DataQuality !== qf) continue;
        if (qv && !`${x.strFullName || ''} ${x.strVehicleNo || ''} ${x.strUnitID || ''}`.toLowerCase().includes(qv)) continue;
        out.push(x);
      }
    }

    if (this.sortKey) {
      const key = this.sortKey;
      const dir = this.sortDir;
      out = [...out].sort((a, b) => dir * ((Number((a as any)[key]) || 0) - (Number((b as any)[key]) || 0)));
    }

    this.filteredRows = out;
    this.cdr.markForCheck();
  }

  toggleSort(key: Exclude<SortKey, null>): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 1 ? -1 : 1;
    } else {
      this.sortKey = key;
      this.sortDir = -1;
    }
    this.applyFilter();
  }

  sortArrow(key: Exclude<SortKey, null>): string {
    if (this.sortKey !== key) return '';
    return this.sortDir === 1 ? ' ▲' : ' ▼';
  }

  onSearchChange(): void {
    this.applyFilter();
  }

  toEn(n: number | string | null | undefined, fraction = 1): string {
    if (n === null || n === undefined || n === '' || isNaN(Number(n))) return '—';
    return Number(n).toLocaleString('en-US', { maximumFractionDigits: fraction });
  }

  badgeClass(s: string): string {
    if (s === 'پرریسک') return 'b-red';
    if (s === 'نیازمند توجه') return 'b-orange';
    if (s === 'نسبتاً ایمن') return 'b-yellow';
    if (s === 'ایمن') return 'b-green';
    return 'b-gray';
  }

  scoreClass(v: number | null | undefined): string {
    const s = Number(v) || 0;
    if (s < 55) return 'red';
    if (s < 65) return 'yellow';
    return 'green';
  }

  qualityClass(q: string): string {
    if (!q) return '';
    if (q.includes('مناسب')) return 'q-ok';
    if (q.includes('پایین')) return 'q-mid';
    return 'q-low';
  }
}

function distinctQualities(rows: VehicleSafetyDetail[]): string[] {
  const set = new Set<string>();
  for (const r of rows) {
    if (r.DataQuality) set.add(r.DataQuality);
  }
  return [...set];
}
